import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ChatCircleDots, CheckCircle, EnvelopeSimple, WarningCircle } from '@phosphor-icons/react';
import { SITE } from '../data/site';
import Container from '../components/ui/Container';
import Reveal, { EASE } from '../components/ui/Reveal';
import PageHeader from '../components/PageHeader';

// Web3Forms 액세스 키.
// 이 값은 클라이언트 번들에 노출되어도 안전한 "공개 키"입니다(설계상 그렇습니다).
// chosw0410@naver.com 으로 발급받은 키이며, 변경 시 https://web3forms.com 에서 재발급.
// (.env에 REACT_APP_WEB3FORMS_KEY를 설정하면 그 값이 우선 적용됩니다)
const WEB3FORMS_PLACEHOLDER = 'YOUR_WEB3FORMS_ACCESS_KEY';
const WEB3FORMS_ACCESS_KEY =
  process.env.REACT_APP_WEB3FORMS_KEY || '3d6d1280-1ade-4c6d-a675-61574fdb9634';

const SERVICE_OPTIONS = ['기업 홈페이지', '관리 시스템', '포트폴리오', '기타'];

const INITIAL_FORM = {
  service: '',
  name: '',
  contact: '',
  message: ''
};

const INPUT_CLASS =
  'w-full rounded-xl border border-line-strong bg-surface px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint outline-none transition-[border-color,box-shadow] duration-200 focus:border-accent focus:ring-4 focus:ring-accent/10';

const CHECKLIST = ['어떤 사이트가 필요한지 (홈페이지, 관리 시스템, 포트폴리오)', '참고하고 싶은 사이트가 있다면 주소', '희망 일정과 예산 범위'];

function Field({ id, label, error, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-[14px] font-semibold text-ink">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="flex items-center gap-1 text-[13px] text-rose-600">
          <WarningCircle size={14} weight="bold" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ChannelLink({ href, icon: Icon, label, value, external }) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      className="group flex items-center gap-4 rounded-2xl bg-white/10 p-4 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-white/15"
    >
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-sky">
        <Icon size={22} weight="duotone" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-white/55">{label}</span>
        <span className="mt-0.5 block truncate text-[15px] font-medium text-white">{value}</span>
      </span>
      <ArrowUpRight size={18} weight="bold" className="shrink-0 text-white/50 transition-colors group-hover:text-white" />
    </a>
  );
}

function ContactPage() {
  const reduce = useReducedMotion();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [statusMessage, setStatusMessage] = useState('');

  const resetStatus = () => {
    if (status !== 'idle') {
      setStatus('idle');
      setStatusMessage('');
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    resetStatus();
  };

  const handleServiceSelect = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
    setErrors((prev) => ({ ...prev, service: '' }));
    resetStatus();
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.service.trim()) {
      nextErrors.service = '관심 서비스를 선택해 주세요.';
    }
    if (!formData.name.trim()) {
      nextErrors.name = '이름을 입력해 주세요.';
    }
    if (!formData.contact.trim()) {
      nextErrors.contact = '연락처를 입력해 주세요.';
    }
    if (!formData.message.trim()) {
      nextErrors.message = '요청사항을 입력해 주세요.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    if (!WEB3FORMS_ACCESS_KEY || WEB3FORMS_ACCESS_KEY === WEB3FORMS_PLACEHOLDER) {
      setStatus('error');
      setStatusMessage('전송 설정이 아직 완료되지 않았습니다. 사이트 관리자에게 문의해 주세요.');
      return;
    }

    setStatus('submitting');
    setStatusMessage('');

    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: `[PageN 문의] ${formData.name}님의 상담 요청`,
      from_name: 'PageN 웹사이트 문의',
      관심서비스: formData.service,
      이름: formData.name,
      연락처: formData.contact,
      요청사항: formData.message,
      botcheck: ''
    };

    // 연락처가 이메일 형태면 회신 주소로 지정해 "답장"이 바로 방문자에게 가도록 함
    if (formData.contact.includes('@')) {
      payload.replyto = formData.contact;
    }

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus('success');
        setStatusMessage('문의가 정상적으로 전송되었습니다. 확인 후 빠르게 답변드리겠습니다.');
        setFormData(INITIAL_FORM);
      } else {
        setStatus('error');
        setStatusMessage(result.message || '전송에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      }
    } catch (error) {
      setStatus('error');
      setStatusMessage('네트워크 오류로 전송하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  const isSubmitting = status === 'submitting';

  return (
    <>
      <PageHeader
        title="프로젝트를 이야기해 주세요"
        description="아래 내용을 남겨 주시면 이메일로 답변드립니다. 급한 문의는 카카오톡 오픈채팅이 더 빠릅니다."
      />

      <section className="pb-20 pt-6 sm:pb-28 sm:pt-10">
        <Container>
          <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
            <Reveal from="left" className="lg:col-span-7" amount={0.1}>
              <form
                onSubmit={handleSubmit}
                noValidate
                className="rounded-4xl border border-line bg-surface p-6 shadow-card sm:p-8 lg:p-10"
              >
                <fieldset className="flex flex-col gap-2">
                  <legend className="text-[14px] font-semibold text-ink">관심 서비스</legend>
                  <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="관심 서비스">
                    {SERVICE_OPTIONS.map((option) => {
                      const selected = formData.service === option;
                      return (
                        <button
                          key={option}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => handleServiceSelect(option)}
                          className={`inline-flex h-10 items-center rounded-full border px-4 text-[14px] font-medium transition-[background-color,color,border-color,transform] duration-200 active:scale-[0.98] ${
                            selected
                              ? 'border-accent bg-accent text-white shadow-accent'
                              : 'border-line-strong bg-surface text-ink-soft hover:border-accent/50 hover:text-ink'
                          }`}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                  <input type="hidden" name="service" value={formData.service} />
                  {errors.service ? (
                    <p className="flex items-center gap-1 text-[13px] text-rose-600">
                      <WarningCircle size={14} weight="bold" />
                      {errors.service}
                    </p>
                  ) : null}
                </fieldset>

                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <Field id="name" label="이름" error={errors.name}>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="성함을 입력해 주세요"
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                      className={INPUT_CLASS}
                    />
                  </Field>
                  <Field id="contact" label="연락처" error={errors.contact}>
                    <input
                      id="contact"
                      name="contact"
                      type="text"
                      autoComplete="email"
                      value={formData.contact}
                      onChange={handleChange}
                      placeholder="이메일 또는 전화번호"
                      aria-invalid={Boolean(errors.contact)}
                      aria-describedby={errors.contact ? 'contact-error' : undefined}
                      className={INPUT_CLASS}
                    />
                  </Field>
                </div>

                <div className="mt-5">
                  <Field id="message" label="요청사항" error={errors.message}>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="필요한 기능, 일정, 예산 범위를 알려주세요. 참고할 사이트가 있다면 주소도 함께 적어 주세요."
                      aria-invalid={Boolean(errors.message)}
                      aria-describedby={errors.message ? 'message-error' : undefined}
                      className={`${INPUT_CLASS} min-h-[160px] resize-y leading-[1.7]`}
                    />
                  </Field>
                </div>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-[13px] leading-[1.6] text-ink-muted">
                    남겨 주신 정보는 문의 답변에만 사용합니다.
                  </p>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-accent px-7 text-base font-semibold text-white shadow-accent transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-accent-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:h-[52px]"
                  >
                    {isSubmitting ? '전송 중' : '문의 보내기'}
                    {isSubmitting ? (
                      <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                    ) : (
                      <ArrowRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5" />
                    )}
                  </button>
                </div>

                <AnimatePresence initial={false}>
                  {status === 'success' && statusMessage ? (
                    <motion.p
                      key="success"
                      role="status"
                      initial={reduce ? false : { opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className="mt-6 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[14px] leading-[1.6] text-emerald-800"
                    >
                      <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
                      {statusMessage}
                    </motion.p>
                  ) : null}
                  {status === 'error' && statusMessage ? (
                    <motion.p
                      key="error"
                      role="alert"
                      initial={reduce ? false : { opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className="mt-6 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[14px] leading-[1.6] text-rose-800"
                    >
                      <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
                      {statusMessage}
                    </motion.p>
                  ) : null}
                </AnimatePresence>
              </form>
            </Reveal>

            <Reveal from="right" delay={0.1} className="lg:col-span-5" amount={0.1}>
              <div className="bg-night-glow relative overflow-hidden rounded-4xl bg-night p-6 text-white sm:p-8">
                <h2 className="text-lg font-bold tracking-tight">바로 연락하기</h2>
                <p className="mt-2 text-[14px] leading-[1.7] text-white/65">
                  폼 대신 아래 채널로 바로 보내셔도 됩니다.
                </p>
                <div className="mt-5 flex flex-col gap-3">
                  <ChannelLink href={`mailto:${SITE.email}`} icon={EnvelopeSimple} label="이메일" value={SITE.email} />
                  <ChannelLink
                    href={SITE.kakao}
                    icon={ChatCircleDots}
                    label="카카오톡 오픈채팅"
                    value="바로 대화하기"
                    external
                  />
                </div>
              </div>

              <div className="mt-6 rounded-4xl bg-lavender p-6 sm:p-8">
                <h2 className="text-base font-bold tracking-tight">문의 전에 정리해 두면 좋은 것</h2>
                <ul className="mt-4 space-y-3">
                  {CHECKLIST.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[14px] leading-[1.7] text-ink-soft">
                      <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-accent" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <dl className="mt-8 grid grid-cols-2 gap-4 px-2 text-[13px] leading-6 text-ink-muted sm:grid-cols-3 lg:grid-cols-2">
                <div>
                  <dt className="font-semibold">상호명</dt>
                  <dd>{SITE.business.company}</dd>
                </div>
                <div>
                  <dt className="font-semibold">대표</dt>
                  <dd>{SITE.business.ceo}</dd>
                </div>
                <div className="col-span-2 sm:col-span-1 lg:col-span-2">
                  <dt className="font-semibold">사업자등록번호</dt>
                  <dd>{SITE.business.registration}</dd>
                </div>
              </dl>
            </Reveal>
          </div>
        </Container>
      </section>
    </>
  );
}

export default ContactPage;
